import { useAuth } from '../contexts/AuthContext';
import { FEATURE_PERMISSION_RULES, FeaturePermission, getRoutePermission } from '../config/permissions';
import { getUserRoles, hasUserRole } from '../utils/userRoles';

export function usePermissions() {
  const { user, hasPermission, hasExactPermission } = useAuth();
  const roles = getUserRoles(user);

  const canAccessPage = (path: string): boolean => {
    if (!user) return false;

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (normalizedPath === '/accounting-workflow' && hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user')) return false;
    const routePermission = getRoutePermission(normalizedPath);
    if (!routePermission) return false;

    return hasPermission(routePermission.module, routePermission.action);
  };

  // Navigation must reflect only the explicitly assigned module. Permission aliases
  // remain available for legacy page actions but cannot expose sibling menu entries.
  const canAccessMenuPage = (path: string): boolean => {
    if (!user) return false;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const routePermission = getRoutePermission(normalizedPath);
    if (!routePermission) return false;
    return hasExactPermission(routePermission.module, routePermission.action);
  };

  const hasFeaturePermission = (feature: FeaturePermission): boolean => {
    if (!user) return false;

    const rules = FEATURE_PERMISSION_RULES[feature] || [];
    return rules.some((rule) => hasPermission(rule.module, rule.action));
  };

  const isAdmin = roles.includes('admin') || roles.includes('super-admin');
  const isDispatcher = roles.includes('dispatcher');
  const isDriver = roles.includes('internal-driver') || roles.includes('external-driver') || roles.includes('corporate-external-transport-user');
  const isWarehouse = roles.includes('warehouse');

  return {
    canAccessPage,
    canAccessMenuPage,
    hasFeaturePermission,
    isAdmin,
    isDispatcher,
    isDriver,
    isWarehouse,
    userRole: user?.role,
    userRoles: roles,
  };
}
